import React, { useCallback, useEffect, useState, forwardRef, useImperativeHandle, useRef } from "react";
import useDebounce from "../../hooks/useDebounce";
import useTranslations from "../../hooks/useTranslations";
import { SearchIcon } from "../icons";

type SearchInputProps = {
  onChange: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  defaultValue?: string;
  liveSearch?: boolean;
  label?: string;
  placeholder?: string;
  id?: string;
};

const SearchInput: React.FC<SearchInputProps> = ({
  onChange,
  onFocus,
  onBlur,
  defaultValue,
  liveSearch = false,
  label,
  id = "search",
  placeholder,
}, ref) => {
  const t = useTranslations();
  const [value, setValue] = useState(defaultValue);
  const debouncedValue = useDebounce(value, 500);

  const _onChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  }, []);

  useEffect(() => {
    console.log("DEB", debouncedValue);
    if (liveSearch) onChange(debouncedValue);
  }, [debouncedValue]);

  useImperativeHandle(ref, () => ({
    clear: () => {
      if (inputRef.current) {
        inputRef.current.value = '';
      }
      setValue("");
    }
  }));

  const inputRef = useRef<HTMLInputElement>(null);

  if (liveSearch)
    return (
      <div className="w-full max-w-lg lg:max-w-sm">
        <label htmlFor="search" className="sr-only">
          {label ? label : t("forms.fields.search")}
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <SearchIcon iconClassName="w-5 h-5 text-gray-400" />
          </div>
          <input
            id={id}
            name="search"
            autoComplete="off"
            defaultValue={defaultValue}
            onChange={_onChange}
            onFocus={onFocus}
            onBlur={onBlur}
            className="block w-full py-2 pl-10 pr-3 leading-5 placeholder-gray-500 bg-white border border-gray-300 rounded-md focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            placeholder={
              placeholder ? placeholder : (t("forms.fields.search") as string)
            }
            type="search"
            ref={inputRef}
          />
        </div>
      </div>
    );

  return (
    <div>
      <label htmlFor="search" className="sr-only">
        {label ? label : t("forms.fields.search")}
      </label>
      <div className="flex rounded-md shadow-sm">
        <div className="relative flex items-stretch flex-grow focus-within:z-10">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <SearchIcon iconClassName="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            name="search"
            onChange={_onChange}
            onFocus={onFocus}
            id={id}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                onChange(value);
              }
            }}
            defaultValue={defaultValue}
            className="block w-full pl-10 border-gray-300 rounded-none focus:ring-primary-500 focus:border-primary-500 rounded-l-md sm:text-sm"
            placeholder={
              placeholder ? placeholder : (t("forms.fields.search") as string)
            }
            ref={inputRef}
          />
        </div>
        <button
          id="searchButton"
          onClick={() => onChange(value)}
          className="relative inline-flex items-center px-4 py-2 -ml-px space-x-2 text-sm font-medium border border-gray-300 text-dark-100 rounded-r-md bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
        >
          <span> {label ? label : t("forms.fields.search")}</span>
        </button>
      </div>
    </div>
  );
};

export default React.memo(forwardRef(SearchInput));
